import assert from 'assert';

export const validateType = (value, name, type) => {
  if (value) {
    assert.equal(
      typeof value,
      type,
      `Variable [${name}] must be of type ${type}`,
    );
  }
  return value;
};

export const VARIABLE_TYPES = ['string', 'number', 'boolean', 'object'];

export const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/* eslint-disable func-names */
export const generateValidators = (variableTypes) => {
  const validators = {};
  validators.required = function (value, name) {
    assert(value, `Variable [${name}] is required`);
  };
  // eslint-disable-next-line no-restricted-syntax
  for (const type of variableTypes) {
    validators[`${type}`] = function (value, name) {
      validateType(value, name, type);
    };
    validators[`required${capitalize(type)}`] = function (value, name) {
      validators.required(validateType(value, name, type), name);
    };
  }

  return validators;
};
/* eslint-enable func-names */

// We generate all potential validators in an object to be called
// eg, validate.string('test','name')
// eg, validate.requiredString('test','name')
/** @type {{[key: string]: (value: any, name: any, type?: any) => void;}} */
export const validate = generateValidators(VARIABLE_TYPES);

// Required boolean is separated from the others because it'll evaluate as
// failing for the case when given false boolean condition
validate.requiredBoolean = (value, name) => {
  assert.notEqual(value, null, `Variable [${name}] must not be null`);
  validateType(value, name, 'boolean');
};

validate.typeOneOf = (value, name, types) => {
  assert(
    // eslint-disable-next-line valid-typeof
    types.some((t) => typeof value === t),
    `Variable [${name}] must be one of ${types}`,
  );
};

validate.unixTimestamp = (value, name) => {
  assert(
    new Date(value).getTime() > 0,
    `Variable [${name}] must be a valid unix timestamp`,
  );
};

// Function to validate latitude and longitude
validate.latLonObject = (value, name) => {
  validate.object(value, name);

  let lat = parseFloat(value.lat);
  let lon = parseFloat(value.lon);

  // Check to make sure lat or lon isn't NaN. Re-assigning the original value to ensure that the error message
  // is consistant with the "requiredNumber" validator
  if (Number.isNaN(lat) || Number.isNaN(lon)) {
    lat = value.lat;
    lon = value.lon;
  }
  validate.requiredNumber(lat, `${name}.lat`);
  validate.requiredNumber(lon, `${name}.lon`);
  assert.equal(
    Object.keys(value).length,
    2,
    `Variable ${name} contains extra properties`,
  );
  assert(lat >= -90, `Variable [${name}.lat] out of bounds`);
  assert(lat <= 90, `Variable [${name}.lat] out of bounds`);
  assert(lon >= -180, `Variable [${name}.lon] out of bounds`);
  assert(lon <= 180, `Variable [${name}.lon] out of bounds`);
};

validate.phoneNumber = (value, name) => {
  // https://blog.kevinchisholm.com/javascript/javascript-e164-phone-number-validation/
  const regEx = /^\+[1-9]\d{10,14}$/;

  assert(
    regEx.test(value),
    `Variable ${name} must be valid e.164 format phone number`,
  );
};

validate.array = (value, name) => {
  assert(Array.isArray(value), `Variable [${name}] must be an array`);
};

validate.stringArray = (value, name) => {
  assert(Array.isArray(value), `Variable [${name}] must be an array`);
  value.forEach((element, index) => {
    validate.string(element, `${name}-index-${index}`);
  });
};

validate.datetimeAfter = (
  firstDateValue,
  firstDateName,
  secondDateValue,
  secondDateName,
) => {
  assert(
    new Date(firstDateValue) < new Date(secondDateValue),
    `Variable ${secondDateName} must be after ${firstDateName}`,
  );
};

export default validate;
